import { BtnPrimary, AmbientParticles } from '../ui/components';
import { currentPlayers } from '../screens/GameSetup';
import { t } from '../i18n';
import { sanitizeHtml } from '../utils/sanitize';
import type { CardsResponse } from '../api/client';
import { state } from '../store';
import { api } from '../api/client';
import { safeStorage } from '../utils/storage';
import { shuffle } from '../utils/random';
import { cardsManager } from './cardsManager';

const VIRAL_STORY_CHANCE = 0.125; // 1/8
const VIRAL_INVITE_CHANCE = 0.1; // 1/10
const VIRAL_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const LS_VIRAL_STORY_LAST = 'pg_viral_story_last';
const LS_VIRAL_INVITE_LAST = 'pg_viral_invite_last';

function isCooledDown(lsKey: string): boolean {
  const last = safeStorage.get(lsKey);
  if (!last) return true;
  return Date.now() - parseInt(last, 10) > VIRAL_COOLDOWN_MS;
}

function markShown(lsKey: string): void {
  safeStorage.set(lsKey, String(Date.now()));
}

function shouldShowViralStory(): boolean {
  if (!isCooledDown(LS_VIRAL_STORY_LAST)) return false;
  return Math.random() < VIRAL_STORY_CHANCE;
}

function shouldShowViralInvite(): boolean {
  if (!isCooledDown(LS_VIRAL_INVITE_LAST)) return false;
  return Math.random() < VIRAL_INVITE_CHANCE;
}


let currentTasks: string[] = [];
let currentIndex: number = 0;
let currentGameTitle: string = '';
let currentGameId: string = 'tod';
let currentPlayerIndex: number = 0;

// ToD-specific state
let todChoiceComplete = false;
let todLastChoice: 'truth' | 'dare' | 'random' | 'random_truth' | 'random_dare' = 'truth';

// Game-specific states
let nhieTappedIds = new Set<string>();
let mltCounting = false;
let mltCount = 3;
let mltInterval: number | null = null;
let viralPenaltyActive = false;
let viralInviteActive = false;

declare global {
  interface Window {
    startTextGame: (title: string, tasks: string[] | CardsResponse, gameId?: string) => void;
    nextTextGameCard: () => Promise<void>;
    handleTodChoice: (choice: string) => Promise<void>;
    toggleNhiePlayer: (id: string) => void;
    startMltCountdown: () => void;
    wyrVote: (option: string) => void;
    shareToStory: () => void;
    skipViralPenalty: () => void;
    shareViralInvite: () => void;
    skipViralInvite: () => void;
  }
}

function resetCardState() {
  todChoiceComplete = false;
  viralPenaltyActive = false;
  viralInviteActive = false;
  nhieTappedIds.clear();
  mltCounting = false;
  mltCount = 3;
  if (mltInterval) {
    clearInterval(mltInterval);
    mltInterval = null;
  }
}

export function cleanupTextGame(): void {
  if (mltInterval) {
    clearInterval(mltInterval);
    mltInterval = null;
  }
  mltCounting = false;
  mltCount = 3;
  todLastChoice = 'truth';
  viralPenaltyActive = false;
  viralInviteActive = false;
  cardsManager.cleanup();
}

window.startTextGame = async (title: string, tasks: string[] | CardsResponse, gameId: string = 'tod') => {
  currentGameTitle = title;
  currentIndex = 0;
  currentPlayerIndex = 0;
  currentGameId = gameId;

  if (gameId === 'tod') {
    // ToD shows choice screen first — no initial card needed
    currentTasks = [];
  } else if (Array.isArray(tasks) && tasks.length > 0) {
    // Legacy direct cards (shouldn't happen in normal flow but keep for safety)
    currentTasks = shuffle([...tasks]);
  } else {
    // cardsManager mode — get first card
    const card = await cardsManager.getNext();
    if (!card) {
      (window as any).showToast?.(t('cards_finished'), 'info');
      window.navTo('games');
      return;
    }
    currentTasks = [card.text];
  }

  resetCardState();
  window.navTo('text-game');
};

window.nextTextGameCard = async () => {
  if (currentGameId === 'tod') {
    currentPlayerIndex = (currentPlayerIndex + 1) % currentPlayers.length;
    todChoiceComplete = false;
  } else {
    currentPlayerIndex = (currentPlayerIndex + 1) % currentPlayers.length;
    const next = await cardsManager.getNext();
    if (next) {
      currentTasks = [next.text];
      currentIndex = 0;
    } else {
      (window as any).showToast?.(t('cards_finished'), 'info');
      window.navTo('games');
      return;
    }
  }

  resetCardState();
  window.renderApp();
};

window.handleTodChoice = async (choice: string) => {
  if (choice === 'truth') {
    todLastChoice = 'truth';
    const card = await cardsManager.getNextOfType('truth');
    currentTasks = [card?.text || t('no_truth_cards')];
  } else if (choice === 'dare') {
    todLastChoice = 'dare';
    const card = await cardsManager.getNextOfType('dare');
    currentTasks = [card?.text || t('no_dare_cards')];
    if (shouldShowViralStory()) {
      viralPenaltyActive = true;
      markShown(LS_VIRAL_STORY_LAST);
    } else if (shouldShowViralInvite()) {
      viralInviteActive = true;
      markShown(LS_VIRAL_INVITE_LAST);
    }
  } else {
    // random — pick any type
    const card = await cardsManager.getNext();
    currentTasks = [card?.text || t('no_cards')];
    todLastChoice = card?.type === 'truth' ? 'random_truth' : 'random_dare';
    if (todLastChoice === 'random_dare') {
      if (shouldShowViralStory()) {
        viralPenaltyActive = true;
        markShown(LS_VIRAL_STORY_LAST);
      } else if (shouldShowViralInvite()) {
        viralInviteActive = true;
        markShown(LS_VIRAL_INVITE_LAST);
      }
    }
  }
  todChoiceComplete = true;
  window.renderApp();
};

window.toggleNhiePlayer = (id: string) => {
  if (nhieTappedIds.has(id)) {
    nhieTappedIds.delete(id);
  } else {
    nhieTappedIds.add(id);
  }
  window.renderApp();
};

window.startMltCountdown = () => {
  mltCounting = true;
  mltCount = 3;
  window.renderApp();
  
  mltInterval = window.setInterval(() => {
    mltCount--;
    if (mltCount <= 0) {
      if (mltInterval) clearInterval(mltInterval);
      window.renderApp();
    } else {
      const countEl = document.getElementById('mlt-countdown-number');
      if (countEl) {
        countEl.textContent = String(mltCount);
      } else {
        window.renderApp();
      }
    }
  }, 1000);
};

window.wyrVote = (option: string) => {
  console.log('Vote:', option);
  // Option selected, advance frame visually or just immediately
  window.nextTextGameCard();
};

window.shareToStory = () => {
  try {
    const tgApp = (window as any).Telegram?.WebApp;
    const lang = (window as any).__state?.user?.lang || 'en';

    const shareText = t('viral_story_text');
    const mediaUrl = lang === 'ru'
      ? 'https://app.pandoragames.party/og-story-card-ru.jpg'
      : 'https://app.pandoragames.party/og-story-card-en.jpg';
    const widgetLink = {
      url: 'https://t.me/pandoragames_bot/app?startapp=story_ref',
      name: 'Party Games 🎮'
    };

    if (tgApp?.shareToStory) {
      tgApp.shareToStory(mediaUrl, {
        text: shareText,
        widget_link: widgetLink
      });
    } else {
      const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(widgetLink.url)}&text=${encodeURIComponent(shareText)}`;
      tgApp?.openTelegramLink?.(shareUrl) || window.open(shareUrl, '_blank');
    }
  } catch (e) {
    console.error('shareToStory error:', e);
  }

  const shareBtn = document.getElementById('viral-share-btn');
  if (shareBtn) {
    shareBtn.innerHTML = t('viral_shared');
    shareBtn.style.pointerEvents = 'none';
  }
  const continueBtn = document.getElementById('viral-continue-btn');
  if (continueBtn) {
    continueBtn.style.display = 'flex';
  }
};

window.skipViralPenalty = () => {
  viralPenaltyActive = false;
  window.renderApp();
};

window.shareViralInvite = async () => {
  try {
    const tgApp = (window as any).Telegram?.WebApp;
    let link = state.referralStats?.referral_link;
    if (!link) {
      const res = await api.getReferral();
      link = res.referral_link;
    }
    const text = t('viral_invite_subtitle');
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
    tgApp?.openTelegramLink?.(shareUrl) || window.open(shareUrl, '_blank');
  } catch (e) {
    console.error('shareViralInvite error:', e);
  }

  const shareBtn = document.getElementById('viral-invite-share-btn');
  if (shareBtn) {
    shareBtn.innerHTML = t('viral_invite_shared');
    shareBtn.style.pointerEvents = 'none';
  }
  const continueBtn = document.getElementById('viral-invite-continue-btn');
  if (continueBtn) {
    continueBtn.style.display = 'flex';
  }
};

window.skipViralInvite = () => {
  viralInviteActive = false;
  window.renderApp();
};

function renderViralPenaltyCard(): string {
  const currentPlayer = currentPlayers[currentPlayerIndex % currentPlayers.length];

  return `
    <div class="absolute inset-0 flex flex-col overflow-y-auto overflow-x-hidden bg-void z-20" id="screen-text-game">
      <div class="fixed top-[-20%] left-[-10%] w-[70vw] h-[60vh] bg-[radial-gradient(ellipse,rgba(160,32,240,0.15)_0%,transparent_70%)] pointer-events-none z-0"></div>
      <div class="fixed bottom-[-10%] right-[-15%] w-[60vw] h-[50vh] bg-[radial-gradient(ellipse,rgba(255,45,107,0.12)_0%,transparent_70%)] pointer-events-none z-0"></div>
      ${AmbientParticles()}

      <!-- Header -->
      <div class="flex flex-col items-center w-full shrink-0 relative z-10 pt-[16px] pb-[8px]">
        <div class="flex flex-col items-center w-full px-[20px] relative">
          <div class="absolute left-[20px] top-[0px] w-[38px] h-[38px] bg-[rgba(255,255,255,0.05)] border border-white/10 rounded-full flex items-center justify-center cursor-pointer text-[18px] transition-all duration-150 active:scale-[0.92] active:bg-[rgba(255,255,255,0.1)] pointer-events-auto z-20" data-action="nav" data-to="games">←</div>
          <div class="font-display text-[20px] font-extrabold text-white leading-[38px] text-center w-full px-[40px] z-10">${sanitizeHtml(currentGameTitle)}</div>
        </div>
        <div class="mt-[12px] bg-[rgba(255,255,255,0.06)] backdrop-blur-md border border-white/10 rounded-pill px-[16px] py-[6px] text-[14px] font-extrabold text-white flex items-center justify-center gap-[6px] shadow-[0_0_12px_rgba(255,255,255,0.05)] text-center w-max mx-auto relative z-10">
          <span class="text-[12px] tracking-[0.5px] uppercase text-t-secondary mr-[2px]">${t('game_turn_label')}</span> <span class="text-[18px] leading-none">${currentPlayer.emoji}</span> <span>${sanitizeHtml(currentPlayer.name)}</span>
        </div>
      </div>

      <!-- Viral Penalty Content -->
      <div class="flex-1 flex flex-col justify-center w-full relative z-10">
        <div class="m-[16px] p-[28px_24px] bg-glass border border-b-glass rounded-xl text-center relative overflow-hidden animate-[taskSlide_0.35s_cubic-bezier(.4,0,.2,1)]">
          <div class="text-[12px] font-extrabold tracking-[2px] uppercase mb-[16px] text-neon-gold">${t('viral_dare_label')}</div>
          <div class="text-[48px] mb-[12px]">📸</div>
          <div class="text-[20px] font-bold text-white leading-[1.5] mb-[8px]">${t('viral_title')}</div>
          <div class="text-[14px] text-t-secondary leading-[1.5]">${t('viral_subtitle')}</div>
        </div>
      </div>

      <!-- Bottom Actions -->
      <div class="p-[20px_16px_32px] relative z-10">
        ${BtnPrimary({
          text: t('viral_share_btn'),
          onClick: 'share-to-story',
          id: 'viral-share-btn'
        })}

        <div id="viral-continue-btn" style="display:none" class="mt-[12px]">
          ${BtnPrimary({
            text: t('viral_continue'),
            onClick: 'skip-viral-penalty'
          })}
        </div>

        <div class="mt-[12px] text-center">
          <button class="bg-transparent border-none text-t-muted text-[13px] font-semibold cursor-pointer p-[8px_16px] transition-all duration-150 active:text-white" data-action="skip-viral-penalty">
            ${t('viral_skip')}
            <div class="text-[11px] text-t-muted/60 mt-[2px]">${t('viral_skip_hint')}</div>
          </button>
        </div>
      </div>
    </div>
  `;
}

function renderViralInviteCard(): string {
  const currentPlayer = currentPlayers[currentPlayerIndex % currentPlayers.length];

  return `
    <div class="absolute inset-0 flex flex-col overflow-y-auto overflow-x-hidden bg-void z-20" id="screen-text-game">
      <div class="fixed top-[-20%] left-[-10%] w-[70vw] h-[60vh] bg-[radial-gradient(ellipse,rgba(0,150,255,0.15)_0%,transparent_70%)] pointer-events-none z-0"></div>
      <div class="fixed bottom-[-10%] right-[-15%] w-[60vw] h-[50vh] bg-[radial-gradient(ellipse,rgba(0,229,255,0.12)_0%,transparent_70%)] pointer-events-none z-0"></div>
      ${AmbientParticles()}

      <!-- Header -->
      <div class="flex flex-col items-center w-full shrink-0 relative z-10 pt-[16px] pb-[8px]">
        <div class="flex flex-col items-center w-full px-[20px] relative">
          <div class="absolute left-[20px] top-[0px] w-[38px] h-[38px] bg-[rgba(255,255,255,0.05)] border border-white/10 rounded-full flex items-center justify-center cursor-pointer text-[18px] transition-all duration-150 active:scale-[0.92] active:bg-[rgba(255,255,255,0.1)] pointer-events-auto z-20" data-action="nav" data-to="games">←</div>
          <div class="font-display text-[20px] font-extrabold text-white leading-[38px] text-center w-full px-[40px] z-10">${sanitizeHtml(currentGameTitle)}</div>
        </div>
        <div class="mt-[12px] bg-[rgba(255,255,255,0.06)] backdrop-blur-md border border-white/10 rounded-pill px-[16px] py-[6px] text-[14px] font-extrabold text-white flex items-center justify-center gap-[6px] shadow-[0_0_12px_rgba(255,255,255,0.05)] text-center w-max mx-auto relative z-10">
          <span class="text-[12px] tracking-[0.5px] uppercase text-t-secondary mr-[2px]">${t('game_turn_label')}</span> <span class="text-[18px] leading-none">${currentPlayer.emoji}</span> <span>${sanitizeHtml(currentPlayer.name)}</span>
        </div>
      </div>

      <!-- Viral Invite Content -->
      <div class="flex-1 flex flex-col justify-center w-full relative z-10">
        <div class="m-[16px] p-[28px_24px] bg-glass border border-b-glass rounded-xl text-center relative overflow-hidden animate-[taskSlide_0.35s_cubic-bezier(.4,0,.2,1)]">
          <div class="text-[12px] font-extrabold tracking-[2px] uppercase mb-[16px] text-neon-cyan">${t('viral_invite_dare_label')}</div>
          <div class="text-[48px] mb-[12px]">📤</div>
          <div class="text-[20px] font-bold text-white leading-[1.5] mb-[8px]">${t('viral_invite_title')}</div>
          <div class="text-[14px] text-t-secondary leading-[1.5]">${t('viral_invite_subtitle')}</div>
        </div>
      </div>

      <!-- Bottom Actions -->
      <div class="p-[20px_16px_32px] relative z-10">
        ${BtnPrimary({
          text: t('viral_invite_btn'),
          onClick: 'share-viral-invite',
          id: 'viral-invite-share-btn'
        })}

        <div id="viral-invite-continue-btn" style="display:none" class="mt-[12px]">
          ${BtnPrimary({
            text: t('viral_continue'),
            onClick: 'skip-viral-invite'
          })}
        </div>

        <div class="mt-[12px] text-center">
          <button class="bg-transparent border-none text-t-muted text-[13px] font-semibold cursor-pointer p-[8px_16px] transition-all duration-150 active:text-white" data-action="skip-viral-invite">
            ${t('viral_skip')}
            <div class="text-[11px] text-t-muted/60 mt-[2px]">${t('viral_skip_hint')}</div>
          </button>
        </div>
      </div>
    </div>
  `;
}

// ─── ToD label helpers ──────────────────────────────────────────────

function getTodLabel(): string {
  if (todLastChoice === 'truth') {
    return `💭 ${t('game_truth').toUpperCase()}`;
  } else if (todLastChoice === 'dare') {
    return `🔥 ${t('game_dare').toUpperCase()}`;
  } else if (todLastChoice === 'random_truth') {
    return `🎲 ${t('game_truth').toUpperCase()}`;
  } else if (todLastChoice === 'random_dare') {
    return `🎲 ${t('game_dare').toUpperCase()}`;
  } else {
    // random — we don't know the original type easily, show generic
    return `🎲 ${t('game_truth_label')}`;
  }
}

function getTodLabelColor(): string {
  if (todLastChoice === 'truth') return 'text-neon-cyan';
  if (todLastChoice === 'dare') return 'text-neon-pink';
  if (todLastChoice === 'random_truth') return 'text-neon-cyan';
  if (todLastChoice === 'random_dare') return 'text-neon-pink';
  return 'text-neon-gold';
}

// ─── Render ─────────────────────────────────────────────────────────

export function renderTextGameEngine(): string {
  const currentText = currentTasks[currentIndex] || currentTasks[0] || '';
  
  let cardHtml = '';
  let bottomHtml = '';

  if (currentGameId === 'tod') {
    if (!todChoiceComplete) {
      // Choice view
      cardHtml = `
        <div class="flex-1 flex flex-col justify-center px-4 w-full">
          <!-- Active Player Indicator placeholder -->
          <div class="text-center pt-5 pb-0">
            <div class="text-[13px] font-bold text-t-secondary tracking-[0.5px] uppercase">${t('game_players_turn')}</div>
            <div class="font-display text-[34px] font-extrabold bg-grad-primary bg-clip-text text-transparent mt-1">${t('game_ready')}</div>
          </div>
          
          <div class="grid grid-cols-2 gap-[12px] p-[24px_0] flex-1 content-center w-full">
            <div class="rounded-xl p-[32px_16px] flex flex-col items-center gap-[10px] cursor-pointer border-[1.5px] border-transparent transition-all duration-200 relative overflow-hidden active:scale-95 bg-[linear-gradient(135deg,rgba(45,107,255,0.25),rgba(0,229,255,0.1))] border-neon-blue/40" data-action="tod-choice" data-choice="truth">
              <div class="text-[40px]">💭</div>
              <div class="font-display text-[20px] font-extrabold text-white">${t('game_truth')}</div>
              <div class="text-[12px] text-white/55 font-bold">${t('game_truth_sub')}</div>
            </div>
            
            <div class="rounded-xl p-[32px_16px] flex flex-col items-center gap-[10px] cursor-pointer border-[1.5px] border-transparent transition-all duration-200 relative overflow-hidden active:scale-95 bg-[linear-gradient(135deg,rgba(255,45,107,0.25),rgba(160,32,240,0.1))] border-neon-pink/40" data-action="tod-choice" data-choice="dare">
              <div class="text-[40px]">🔥</div>
              <div class="font-display text-[20px] font-extrabold text-white">${t('game_dare')}</div>
              <div class="text-[12px] text-white/55 font-bold">${t('game_dare_sub')}</div>
            </div>
          </div>
          
          <div class="m-[0_0_16px] p-[15px] bg-glass border border-white/12 rounded-full text-center text-[15px] font-bold text-t-secondary cursor-pointer transition-all duration-150 active:bg-white/12 active:text-white" data-action="tod-choice" data-choice="random">
            ${t('game_random')}
          </div>
        </div>
      `;
    } else if (viralPenaltyActive) {
      return renderViralPenaltyCard();
    } else if (viralInviteActive) {
      return renderViralInviteCard();
    } else {
      // Task view — with correct truth/dare label
      const label = getTodLabel();
      const labelColor = getTodLabelColor();
      
      cardHtml = `
        <div class="flex-1 flex flex-col justify-center w-full">
          <div class="m-[16px] p-[28px_24px] bg-glass border border-b-glass rounded-xl text-center relative overflow-hidden animate-[taskSlide_0.35s_cubic-bezier(.4,0,.2,1)]">
            <div class="text-[12px] font-extrabold tracking-[2px] uppercase mb-[16px] ${labelColor}">${label}</div>
            <div class="text-[20px] font-bold text-white leading-[1.5]">${sanitizeHtml(currentText)}</div>
          </div>
        </div>
      `;
      bottomHtml = `
        <div class="p-[20px_16px_32px]">
          ${BtnPrimary({
            text: t('game_next_player'),
            onClick: 'next-text-card'
          })}
        </div>
      `;
    }
  } else if (currentGameId === 'nhie') {
    const gridHtml = currentPlayers.map(p => {
      const isTapped = nhieTappedIds.has(p.id);
      const bgClass = isTapped ? 'bg-neon-pink/20 border-neon-pink shadow-[0_0_16px_rgba(255,45,107,0.3)]' : 'bg-glass border-b-glass';
      const emojiSuffix = isTapped ? ' 🍺' : '';
      return `
        <div class="p-[16px] ${bgClass} border-[1.5px] rounded-lg text-center cursor-pointer transition-all duration-200" data-action="nhie-toggle-player" data-player-id="${p.id}">
          <div class="text-[24px]">${p.emoji}${emojiSuffix}</div>
          <div class="text-[14px] font-extrabold mt-[6px]">${sanitizeHtml(p.name)}</div>
        </div>
      `;
    }).join('');

    cardHtml = `
      <div class="m-[16px] min-h-[200px] flex flex-col items-center justify-center p-[32px_24px] bg-[linear-gradient(135deg,rgba(255,45,107,0.12),rgba(160,32,240,0.08))] border border-neon-pink/25 rounded-xl text-center animate-[taskSlide_0.35s_cubic-bezier(.4,0,.2,1)] shrink-0">
        <div class="text-[22px] font-extrabold text-white leading-[1.4]">${sanitizeHtml(currentText)}</div>
      </div>
      <div class="p-[0_16px_12px]">
        <div class="text-[13px] font-bold text-t-secondary text-center">${t('game_nhie_tap')}</div>
      </div>
      <div class="grid grid-cols-2 gap-[10px] p-[0_16px] w-full">
        ${gridHtml}
      </div>
    `;
    bottomHtml = `
      <div class="p-[20px_16px_32px] mt-auto">
        ${BtnPrimary({
          text: t('game_next_card'),
          onClick: 'next-text-card'
        })}
      </div>
    `;
  } else if (currentGameId === 'mlt') {
    if (mltCounting && mltCount > 0) {
      cardHtml = `
        <div class="flex-1 flex flex-col items-center justify-center text-center p-[40px_20px] w-full z-10 relative">
          <div class="animate-iconFloat" style="font-size:min(50vw, 240px);line-height:1;transform-origin:center center;filter:drop-shadow(0 0 24px rgba(0,229,255,0.4))">☝️</div>
          <div id="mlt-countdown-number" class="font-display text-[120px] font-extrabold bg-grad-primary bg-clip-text text-transparent animate-[timerWarning_0.5s_infinite_alternate] leading-none mt-[16px]">${mltCount}</div>
          <div class="text-[20px] font-bold text-t-secondary mt-[24px]">${t('game_mlt_point_ready')}</div>
        </div>
      `;
      bottomHtml = '';
    } else if (mltCounting && mltCount <= 0) {
      cardHtml = `
        <div class="flex-1 flex flex-col items-center justify-center text-center p-[40px_20px] w-full z-10 relative">
          <div class="animate-iconFloat" style="font-size:min(50vw, 240px);line-height:1;transform-origin:center center;filter:drop-shadow(0 0 24px rgba(0,229,255,0.4))">☝️</div>
          <div class="font-display text-[28px] font-extrabold text-neon-cyan animate-[iconPulse_2s_ease-in-out_infinite] leading-[1.2] mt-[24px]">${t('game_mlt_point_now')}</div>
        </div>
      `;
      bottomHtml = `
        <div class="p-[20px_16px_32px] text-center w-full mt-auto z-10 relative">
          ${BtnPrimary({
            text: t('game_next_q'),
            onClick: 'next-text-card'
          })}
        </div>
      `;
    } else {
      cardHtml = `
        <div class="w-full relative z-10">
          <div class="m-[16px] min-h-[180px] flex flex-col items-center justify-center p-[32px_24px] bg-[linear-gradient(135deg,rgba(0,229,255,0.1),rgba(45,107,255,0.06))] border border-neon-cyan/25 rounded-xl text-center animate-[taskSlide_0.35s_cubic-bezier(.4,0,.2,1)]">
            <div class="text-[22px] font-extrabold text-white leading-[1.4]">${sanitizeHtml(currentText)}</div>
          </div>
        </div>
      `;
      bottomHtml = `
        <div class="p-[20px_16px_32px] text-center w-full mt-auto z-10 relative">
          <div class="text-[15px] font-bold text-t-secondary mb-[8px]">${t('game_mlt_ready')}</div>
          <div class="text-[13px] font-semibold text-t-muted mb-[20px]">${t('game_mlt_sub')}</div>
          ${BtnPrimary({
            text: t('game_mlt_count_btn'),
            onClick: 'mlt-count-start'
          })}
        </div>
      `;
    }
  } else if (currentGameId === 'wyr') {
    const parts = currentText.includes('|') ? currentText.split('|').map(p => p.trim()) : [currentText, currentText];
    
    cardHtml = `
      <div class="flex-1 flex flex-col w-full p-[8px_16px_0]">
        <div class="text-center text-[13px] font-bold text-t-secondary mb-[16px]">${t('game_wyr_sub')}</div>
        <div class="rounded-lg p-[24px_20px] border-[2px] border-neon-pink/35 text-center text-[16px] font-bold text-white leading-[1.5] cursor-pointer transition-transform active:scale-95 bg-[linear-gradient(135deg,rgba(255,45,107,0.2),rgba(160,32,240,0.1))] relative overflow-hidden" data-action="wyr-vote" data-choice="a">
          <div class="text-[13px] font-extrabold text-neon-pink/80 mb-[8px] tracking-[1px]">${t('game_option_a')}</div>
          <div>${sanitizeHtml(parts[0])}</div>
        </div>
        
        <div class="text-center font-display text-[20px] font-extrabold bg-grad-premium bg-clip-text text-transparent p-[12px_0]">${t('game_or')}</div>
        
        <div class="rounded-lg p-[24px_20px] border-[2px] border-neon-blue/35 text-center text-[16px] font-bold text-white leading-[1.5] cursor-pointer transition-transform active:scale-95 bg-[linear-gradient(135deg,rgba(45,107,255,0.2),rgba(0,229,255,0.08))] relative overflow-hidden" data-action="wyr-vote" data-choice="b">
          <div class="text-[13px] font-extrabold text-neon-blue/80 mb-[8px] tracking-[1px]">${t('game_option_b')}</div>
          <div>${sanitizeHtml(parts[1] || parts[0])}</div>
        </div>
        
        <div class="mt-8 text-center pt-5">
           <button class="flex items-center justify-center gap-[8px] w-full p-[15px_24px] bg-glass border border-b-glass rounded-full text-white font-body text-[16px] font-bold cursor-pointer backdrop-blur-[12px] transition-all duration-150 active:scale-95 active:bg-white/10" data-action="next-text-card">
             ${t('game_next_q')}
           </button>
        </div>
      </div>
    `;
    bottomHtml = ''; 
  } else {
    // Default / Unknown logic
    cardHtml = `
      <div class="m-[16px] min-h-[200px] flex flex-col items-center justify-center p-[32px_24px] bg-[linear-gradient(135deg,rgba(255,45,107,0.12),rgba(160,32,240,0.08))] border border-neon-pink/25 rounded-xl text-center animate-[taskSlide_0.35s_cubic-bezier(.4,0,.2,1)] shrink-0">
        <div class="text-[13px] font-bold text-t-muted tracking-[1px] mb-[8px] uppercase">TEXT GAME</div>
        <div class="text-[22px] font-extrabold text-white leading-[1.4]">${sanitizeHtml(currentText)}</div>
      </div>
    `;
    bottomHtml = `
      <div class="p-[20px_16px_32px] mt-auto">
        ${BtnPrimary({
          text: t('game_next_card'),
          onClick: 'next-text-card'
        })}
      </div>
    `;
  }
  
  const currentPlayer = currentPlayers[currentPlayerIndex % currentPlayers.length];

  return `
    <div class="absolute inset-0 flex flex-col overflow-y-auto overflow-x-hidden bg-void z-20" id="screen-text-game">
      <!-- Background Blurs -->
      <div class="fixed top-[-20%] left-[-10%] w-[70vw] h-[60vh] bg-[radial-gradient(ellipse,rgba(160,32,240,0.15)_0%,transparent_70%)] pointer-events-none z-0"></div>
      <div class="fixed bottom-[-10%] right-[-15%] w-[60vw] h-[50vh] bg-[radial-gradient(ellipse,rgba(255,45,107,0.12)_0%,transparent_70%)] pointer-events-none z-0"></div>
      ${AmbientParticles()}
      
      <!-- Header -->
      <div class="flex flex-col items-center w-full shrink-0 relative z-10 pt-[16px] pb-[8px]">
        <div class="flex flex-col items-center w-full px-[20px] relative">
           <div class="absolute left-[20px] top-[0px] w-[38px] h-[38px] bg-[rgba(255,255,255,0.05)] border border-white/10 rounded-full flex items-center justify-center cursor-pointer text-[18px] transition-all duration-150 active:scale-[0.92] active:bg-[rgba(255,255,255,0.1)] pointer-events-auto z-20" data-action="nav" data-to="games">←</div>
           <div class="font-display text-[20px] font-extrabold text-white leading-[38px] text-center w-full px-[40px] z-10">${sanitizeHtml(currentGameTitle)}</div>
        </div>
        <div class="mt-[12px] bg-[rgba(255,255,255,0.06)] backdrop-blur-md border border-white/10 rounded-pill px-[16px] py-[6px] text-[14px] font-extrabold text-white flex items-center justify-center gap-[6px] shadow-[0_0_12px_rgba(255,255,255,0.05)] text-center w-max mx-auto relative z-10">
          <span class="text-[12px] tracking-[0.5px] uppercase text-t-secondary mr-[2px]">${t('game_turn_label')}</span> <span class="text-[18px] leading-none">${currentPlayer.emoji}</span> <span>${sanitizeHtml(currentPlayer.name)}</span>
        </div>
      </div>
      
      <!-- Dynamic Content Area -->
      ${cardHtml}
      
      <!-- Dynamic Bottom Area -->
      ${bottomHtml}
      
    </div>
  `;
}
